import { DAY_AHEAD_BID_VALUE_DECIMALS } from 'helpers/elplan.helper/bidValue.helper'
import type { DayAheadBidValueFault } from 'helpers/elplan.helper/bidValue.helper.types'
import type { ElplanAlertMessage } from './elplan.alert.types'
import { BID_TYPE_ALERT_KEY_BY_TOKEN } from './elplan.alertVocabulary.helper'
import {
  PRICE_REQUIRED_CODE,
  SPAN_SHORTER_THAN_MINIMUM_CODE,
  VOLUME_PRECISION_TOO_HIGH_CODE,
} from './elplan.cellFault.helper'
import type { ElplanDayTableCellFault } from './elplan.cellFault.types'

const UNKNOWN_CODE_ALERT_KEY = 'The server rejected this value: {{message}}'
const SPAN_SHORTER_THAN_MINIMUM_ALERT_KEY =
  'A {{bidType}} must span at least {{minimumHours}} hours; this one spans {{spanHours}}.'
const SPAN_SHORTER_THAN_MINIMUM_PLAIN_ALERT_KEY = 'This bid is shorter than the minimum span for its bid type.'

function buildDecimalLimitAlert(decimals: number): ElplanAlertMessage {
  return { key: 'Maximum {{count}} decimals allowed', values: { count: decimals } }
}

function resolveSpanShorterThanMinimumAlert({ span }: ElplanDayTableCellFault): ElplanAlertMessage {
  const bidType = span && BID_TYPE_ALERT_KEY_BY_TOKEN[span.bidType]
  if (span === undefined || bidType === undefined) {
    return { key: SPAN_SHORTER_THAN_MINIMUM_PLAIN_ALERT_KEY }
  }

  return {
    key: SPAN_SHORTER_THAN_MINIMUM_ALERT_KEY,
    values: { minimumHours: span.minimumHours, spanHours: span.spanHours },
    translatedValues: { bidType },
  }
}

const CELL_FAULT_ALERT_RESOLVER_BY_CODE: Record<string, (fault: ElplanDayTableCellFault) => ElplanAlertMessage> = {
  [PRICE_REQUIRED_CODE]: () => ({ key: 'A block or profile bid needs a price.' }),
  [VOLUME_PRECISION_TOO_HIGH_CODE]: () => buildDecimalLimitAlert(DAY_AHEAD_BID_VALUE_DECIMALS),
  [SPAN_SHORTER_THAN_MINIMUM_CODE]: resolveSpanShorterThanMinimumAlert,
}

export function resolveElplanCellFaultAlert(fault: ElplanDayTableCellFault): ElplanAlertMessage {
  return (
    CELL_FAULT_ALERT_RESOLVER_BY_CODE[fault.code]?.(fault) ?? {
      key: UNKNOWN_CODE_ALERT_KEY,
      values: { message: fault.message ?? fault.code },
    }
  )
}

export const DAY_AHEAD_BID_NAME_WITHOUT_VOLUME_ALERT: ElplanAlertMessage = {
  key: 'Add a volume, or clear the name and price to remove this bid.',
}

export const DAY_AHEAD_BID_PRICE_WITHOUT_VOLUME_ALERT: ElplanAlertMessage = {
  key: 'Add a volume, or clear the price to remove this bid.',
}

export function resolveDayAheadBidValueFaultAlert(fault: DayAheadBidValueFault): ElplanAlertMessage {
  return fault === 'not-a-number'
    ? { key: 'This value is not a number.' }
    : buildDecimalLimitAlert(DAY_AHEAD_BID_VALUE_DECIMALS)
}
